import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { extractNumericId } from '../../utils/formatters';
import useCustomer from '../../hooks/useCustomer';
import Card from '../../common/Card';
import Button from '../../common/Button';
import SearchInput from '../../common/SearchInput';
import Loading from '../../common/Loading';
import ErrorMessage from '../../common/ErrorMessage';
import Pagination from '../../common/Pagination';
import CustomerCard from '../../common/CustomerCard';
import './styles.css';

/**
 * 客戶列表組件
 * 用於顯示所有客戶
 * 
 * @param {Object} props - 組件屬性
 * @param {string} props.token - 認證令牌
 * @returns {JSX.Element} 客戶列表組件
 */
function CustomerList({ token }) {
    // 使用自定義Hook
    const {
        customers,
        loading,
        error,
        searchCustomers,
        sortCustomers
    } = useCustomer(token);

    // 本地狀態
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('lastName');
    const [sortDirection, setSortDirection] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(12); // 增加每頁顯示數量，適合卡片佈局
    const [viewMode, setViewMode] = useState('table'); // 默認為表格視圖

    // 使用 useMemo 優化搜索和排序
    const filteredCustomers = useMemo(() => {
        if (!customers || customers.length === 0) return [];
        
        const searchResults = searchCustomers(searchTerm);
        return sortCustomers(sortField, sortDirection, searchResults);
    }, [searchTerm, sortField, sortDirection, searchCustomers, sortCustomers, customers]);

    // 當搜索條件改變時，重置頁碼
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // 處理排序
    const handleSortClick = (field) => {
        if (field === sortField) {
            // 如果點擊的是當前排序欄位，則切換排序方向
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // 否則，設置新的排序欄位，並默認為升序
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // 獲取排序圖標
    const getSortIcon = (field) => {
        if (field !== sortField) return null;

        return sortDirection === 'asc' ? (
            <span className="sort-icon">▲</span>
        ) : (
            <span className="sort-icon">▼</span>
        );
    };

    // 分頁邏輯
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

    // 處理頁碼變更
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // 切換視圖模式
    const toggleViewMode = () => {
        setViewMode(viewMode === 'grid' ? 'table' : 'grid');
    };

    // 加載中狀態
    if (loading) {
        return <Loading text="正在加載客戶數據..." />;
    }

    // 錯誤狀態
    if (error) {
        return (
            <ErrorMessage
                message={error}
                onRetry={() => window.location.reload()}
            />
        );
    }

    return (
        <div className="customer-list-container">
            {/* 頁面標題 */}
            <div className="customer-list-header">
                <h2 className="customer-list-title">客戶管理</h2>
                <Link to="/customers/add">
                    <Button variant="primary">
                        新增客戶
                    </Button>
                </Link>
            </div>

            {/* 搜索和過濾 */}
            <Card className="customer-list-search-card">
                <div className="customer-list-search-container">
                    <SearchInput
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder="搜索客戶姓名、電子郵件或電話..."
                        debounceTime={150}
                    />
                    <div className="customer-list-view-toggle">
                        <Button
                            variant={viewMode === 'table' ? 'secondary' : 'primary'}
                            onClick={toggleViewMode}
                        >
                            {viewMode === 'table' ? '卡片視圖' : '表格視圖'}
                        </Button>
                    </div>
                </div>
            </Card>

            {/* 客戶列表 */}
            {filteredCustomers.length === 0 ? (
                <Card className="customer-list-empty-card">
                    <div className="customer-list-empty">
                        <p>沒有找到符合條件的客戶</p>
                    </div>
                </Card>
            ) : (
                <Card className="customer-list-content-card">
                    {viewMode === 'grid' ? (
                        // 網格視圖
                        <div className="customer-list-grid">
                            {currentItems.map((customer) => (
                                <CustomerCard
                                    key={customer.id}
                                    customer={customer}
                                    showDetails={false}
                                />
                            ))}
                        </div>
                    ) : (
                        // 表格視圖
                        <div className="customer-list-table-container">
                            <table className="customer-list-table">
                                <thead>
                                    <tr>
                                        <th
                                            onClick={() => handleSortClick('id')}
                                            className="sortable-header"
                                        >
                                            客戶ID {getSortIcon('id')}
                                        </th>
                                        <th
                                            onClick={() => handleSortClick('lastName')}
                                            className="sortable-header"
                                        >
                                            姓名 {getSortIcon('lastName')}
                                        </th>
                                        <th
                                            onClick={() => handleSortClick('email')}
                                            className="sortable-header"
                                        >
                                            電子郵件 {getSortIcon('email')}
                                        </th>
                                        <th
                                            onClick={() => handleSortClick('nfc_id')}
                                            className="sortable-header"
                                        >
                                            NFC ID {getSortIcon('nfc_id')}
                                        </th>
                                        <th
                                            onClick={() => handleSortClick('level')}
                                            className="sortable-header"
                                        >
                                            客戶等級 {getSortIcon('level')}
                                        </th>
                                        <th>操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentItems.map((customer) => (
                                        <tr key={customer.id}>
                                            <td>{extractNumericId(customer.id)}</td>
                                            <td>
                                                {customer.firstName} {customer.lastName}
                                            </td>
                                            <td>{customer.email || '未設置'}</td>
                                            <td>{customer.nfc_id || '未設置'}</td>
                                            <td>{customer.level || '普通'}</td>
                                            <td>
                                                <div className="customer-list-actions">
                                                    <Link
                                                        to={`/customers/${extractNumericId(customer.id)}`}
                                                        className="customer-list-action-link"
                                                    >
                                                        查看
                                                    </Link>
                                                    <Link
                                                        to={`/customers/${extractNumericId(customer.id)}/edit`}
                                                        className="customer-list-action-link"
                                                    >
                                                        編輯
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* 分頁 */}
                    <div className="customer-list-pagination">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </Card>
            )}
        </div>
    );
}

export default CustomerList;